import { Divisor, useDivisor } from '../components/Divisor';
import { Icon } from '@iconify/react';
import { useState } from 'react';

import {
  Barra,
  Card,
  Carregando,
  ErroInline,
  Kpi,
  Ponto,
  Segmented,
  Sparkline,
  Tag,
  TagComPonto,
  TituloTela,
} from '../components/Sga';
import { agruparConsecutivos } from '../lib/agrupar';
import { ddmmHora, duracao, hhmm, kindDoContainer, kindDoNivel, n, pct } from '../lib/format';
import type { Dashboard as DashboardDto, Kind } from '../lib/types';

type Visao = 'cards' | 'densa';

export function Dashboard({
  dados,
  carregando,
  erro,
  aoRecarregar,
  tenantNome,
  aoVerLogs,
}: {
  dados: DashboardDto | null;
  carregando: boolean;
  erro: string | null;
  aoRecarregar: () => void;
  tenantNome: string;
  aoVerLogs: () => void;
}) {
  const divisor = useDivisor({
    chave: 'dashboard',
    inicial: 53,
    min: 35,
    max: 72,
    passo: 2,
    calcular: (clientX, area) => ((clientX - area.left) / area.width) * 100,
    rotulo: 'Divisória entre sistemas integrados e incidentes',
  });

  const [visao, setVisao] = useState<Visao>('cards');

  if (erro) return <ErroInline mensagem={erro} aoTentar={aoRecarregar} />;
  if (!dados) return <Carregando texto="Levantando saúde e integrações…" />;

  // Recarga em andamento com dados na tela: indica sem piscar o conteúdo.
  const contextoRecarga = carregando ? ' · atualizando…' : '';

  const { kpis, containers, integracoes, incidentes, fila } = dados;
  const riscoKind: Kind =
    kpis.risco_ban.rotulo === 'Crítico' ? 'err' : kpis.risco_ban.rotulo === 'Atenção' ? 'warn' : 'ok';

  // O painel tem que responder "está tudo bem?" antes de mostrar números.
  // Antes, tudo tinha o mesmo peso visual e era preciso ler a tela inteira.
  const foraDoAr = kpis.conexoes_ativas.total - kpis.conexoes_ativas.ok;
  const containersRuins = containers.filter((c) => c.status !== 'online');
  const problemas: { texto: string; kind: Kind }[] = [
    ...(foraDoAr > 0
      ? [{ texto: `${foraDoAr} ${foraDoAr === 1 ? 'conexão fora do ar' : 'conexões fora do ar'}`, kind: 'err' as Kind }]
      : []),
    ...(containersRuins.length > 0
      ? [
          {
            texto:
              containersRuins.length === 1
                ? `container ${containersRuins[0].nome} degradado`
                : `${containersRuins.length} containers degradados`,
            kind: 'err' as Kind,
          },
        ]
      : []),
    ...(fila.falhas_24h > 0
      ? [{ texto: `${n(fila.falhas_24h)} falhas em 24 h`, kind: 'err' as Kind }]
      : []),
    ...(fila.retidas > 0
      ? [{ texto: `${n(fila.retidas)} retidas pelo Anti-Ban`, kind: 'warn' as Kind }]
      : []),
    ...(riscoKind !== 'ok'
      ? [{ texto: `Risco de ban ${kpis.risco_ban.rotulo.toLowerCase()}`, kind: riscoKind }]
      : []),
  ];
  const gravidade: Kind = problemas.some((p) => p.kind === 'err')
    ? 'err'
    : problemas.length > 0
      ? 'warn'
      : 'ok';

  // Incidentes repetidos colapsam: seis linhas idênticas informam uma vez.
  const incidentesAgrupados = agruparConsecutivos(
    incidentes,
    (i) => `${i.nivel}|${i.origem}|${i.evento}|${i.codigo ?? ''}`,
  );

  return (
    <>
      <TituloTela
        icone="ant-design:dashboard-outlined"
        titulo="Saúde e integração"
        contexto={`${tenantNome} — atualizado ${hhmm(dados.atualizado_em)}${contextoRecarga}`}
        acoes={
          <Segmented
            valor={visao}
            onChange={setVisao}
            opcoes={[
              { valor: 'cards', rotulo: 'Cartões' },
              { valor: 'densa', rotulo: 'Tabela densa' },
            ]}
          />
        }
      />

      <div
        className={`sga-atencao sga-atencao--${gravidade}`}
        role="status"
        aria-live="polite"
      >
        <span className="sga-atencao__icone">
          <Icon
            icon={
              gravidade === 'ok'
                ? 'mdi:check-circle-outline'
                : gravidade === 'warn'
                  ? 'mdi:alert-circle-outline'
                  : 'mdi:close-octagon-outline'
            }
            width={20}
            style={{
              color:
                gravidade === 'ok'
                  ? 'var(--sga-sucesso-texto)'
                  : gravidade === 'warn'
                    ? 'var(--sga-warning-texto)'
                    : 'var(--sga-error-texto)',
            }}
          />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="sga-atencao__titulo">
            {gravidade === 'ok'
              ? 'Operação normal'
              : gravidade === 'warn'
                ? 'Atenção — a operação segue, mas há retenção'
                : 'Ação necessária'}
          </div>
          <div className="sga-atencao__sub">
            {gravidade === 'ok'
              ? `${kpis.conexoes_ativas.ok} de ${kpis.conexoes_ativas.total} conexões online, fila fluindo, nenhum container degradado.`
              : 'Itens abaixo precisam de conferência.'}
          </div>
        </div>
        {problemas.length > 0 && (
          <div className="sga-atencao__itens">
            {problemas.map((p) => (
              <Tag key={p.texto} kind={p.kind} solido>
                {p.texto}
              </Tag>
            ))}
          </div>
        )}
      </div>

      <div className="sga-grid sga-grid--4">
        <Kpi
          icone="mdi:lan-connect"
          rotulo="Conexões ativas"
          valor={`${kpis.conexoes_ativas.ok}/${kpis.conexoes_ativas.total}`}
          nota={`${kpis.conexoes_ativas.total - kpis.conexoes_ativas.ok} fora do ar`}
        />
        <Kpi
          icone="mdi:message-processing-outline"
          rotulo="Mensagens hoje"
          valor={n(kpis.mensagens_hoje)}
          nota={
            kpis.mensagens_hoje === 0
              ? 'nenhuma mensagem hoje'
              : kpis.variacao_vs_ontem_pct === 0
                ? 'sem base de comparação'
                : `${kpis.variacao_vs_ontem_pct > 0 ? '+' : ''}${kpis.variacao_vs_ontem_pct}% vs. ontem`
          }
        />
        <Kpi
          icone="mdi:format-list-numbered"
          rotulo="Fila atual"
          valor={n(kpis.fila_atual)}
          nota={`${n(fila.retidas)} retidas pelo Anti-Ban`}
          cor="var(--sga-warning)"
        />
        <Kpi
          icone="mdi:shield-check-outline"
          rotulo="Risco de ban"
          valor={kpis.risco_ban.rotulo}
          cor={
            riscoKind === 'err'
              ? 'var(--sga-error-texto)'
              : riscoKind === 'warn'
                ? 'var(--sga-warning-texto)'
                : undefined
          }
          nota={
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span className="sga-medidor" style={{ flex: 1, height: 5 }}>
                <span
                  style={{
                    display: 'block',
                    width: `${kpis.risco_ban.pct}%`,
                    height: '100%',
                    borderRadius: 999,
                    background:
                      riscoKind === 'err'
                        ? 'var(--sga-error)'
                        : riscoKind === 'warn'
                          ? 'var(--sga-warning)'
                          : 'var(--sga-tag-text)',
                  }}
                />
              </span>
              {pct(kpis.risco_ban.pct)}
            </span>
          }
        />
      </div>

      {visao === 'cards' ? (
        <div className="sga-grid sga-grid--containers">
          {containers.map((c) => (
            <Card key={c.servico} style={{ padding: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Icon icon={iconeContainer(c.servico)} width={24} style={{ color: 'var(--sga-primary-text)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={c.nome}
                  >
                    {c.nome}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--sga-fg-3-texto)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.versao ?? '—'} · {c.nos ?? '1 nó'}
                  </div>
                </div>
                <TagComPonto kind={kindDoContainer(c.status)}>{c.status}</TagComPonto>
              </div>
              <Sparkline serie={c.serie ?? []} kind={kindDoContainer(c.status)} />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                  color: 'var(--sga-fg-3)',
                  marginTop: 8,
                }}
              >
                <span data-zero={c.msg_hora ? undefined : 'true'}>{n(c.msg_hora)} msg/h</span>
                <span data-zero={c.uptime_pct === null ? 'true' : undefined}>
                  {c.uptime_pct !== null ? `${c.uptime_pct.toFixed(1)}%` : '—'}
                </span>
                <span className="mono" data-zero={c.latencia_ms ? undefined : 'true'}>
                  {duracao(c.latencia_ms)}
                </span>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card titulo="Containers">
          <div className="sga-tabela-scroll">
            <table className="sga-table sga-table--densa">
              <thead>
                <tr>
                  <th>Container</th>
                  <th>Versão</th>
                  <th>Nós</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>msg/h</th>
                  <th>Uptime 7d</th>
                  <th>Latência</th>
                  <th>Reinícios 24h</th>
                  <th>Última troca</th>
                </tr>
              </thead>
              <tbody>
                {containers.map((c) => (
                  <tr key={c.servico}>
                    <td>{c.nome}</td>
                    <td className="mono">{c.versao ?? '—'}</td>
                    <td>{c.nos ?? '1 nó'}</td>
                    <td>
                      <TagComPonto kind={kindDoContainer(c.status)}>{c.status}</TagComPonto>
                    </td>
                    <td className="num">{n(c.msg_hora)}</td>
                    <td>{c.uptime_pct !== null ? `${c.uptime_pct.toFixed(1)}%` : '—'}</td>
                    <td className="mono">{duracao(c.latencia_ms)}</td>
                    <td className="num">{c.restarts_24h}</td>
                    <td className="mono">{c.ultima_troca ? ddmmHora(c.ultima_troca) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div
        className="sga-grid sga-grid--dash"
        ref={divisor.areaRef}
        style={{ gridTemplateColumns: `minmax(0, ${divisor.valor}fr) auto minmax(0, ${100 - divisor.valor}fr)` }}
      >
        <Card titulo="Sistemas integrados">
          {integracoes.length === 0 && (
            <div className="sga-vazio">Nenhum sistema cadastrado neste tenant.</div>
          )}
          {integracoes.map((s) => (
            <div key={s.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--sga-border-gray)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Ponto kind={s.integrado ? 'ok' : 'mut'} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5 }}>{s.sistema}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--sga-fg-3)' }}>{s.tipo}</div>
                </div>
                <Tag kind={s.integrado ? 'ok' : 'mut'}>
                  {s.integrado ? 'Integrado' : 'Não integrado'}
                </Tag>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 11.5,
                  color: 'var(--sga-fg-3)',
                  margin: '6px 0 5px',
                }}
              >
                <span>
                  {n(s.volume)} {s.metrica ?? 'eventos'}
                </span>
                <span style={{ flex: 1 }} />
                <span className="mono">
                  {s.latencia_p95_ms ? `p95 ${duracao(s.latencia_p95_ms)}` : '—'}
                </span>
              </div>
              <Barra pct={s.progresso_pct} altura={6} kind={s.integrado ? 'ok' : 'mut'} />
            </div>
          ))}
        </Card>

        <Divisor {...divisor.props} />

        <Card
          titulo="Incidentes recentes"
          acoes={
            <button type="button" className="btn btn-default btn-sm" onClick={aoVerLogs}>
              Ver todos os logs
            </button>
          }
        >
          {incidentes.length === 0 && (
            <div className="sga-vazio">
              <Icon icon="mdi:check-circle-outline" width={22} />
              Nenhum incidente nas últimas horas.
            </div>
          )}
          {incidentesAgrupados.map(({ item: i, vezes }) => (
            <div
              key={i.id}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: '9px 0',
                borderBottom: '1px solid var(--sga-border-gray)',
              }}
            >
              <Icon
                icon={
                  i.nivel === 'banimento'
                    ? 'mdi:close-octagon-outline'
                    : i.nivel === 'erro'
                      ? 'mdi:alert-circle-outline'
                      : 'mdi:timer-sand'
                }
                width={18}
                style={{ color: corDoNivel(i.nivel), marginTop: 2, flex: 'none' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {i.evento}
                  </span>
                  {vezes > 1 && (
                    <span className="sga-repeticao" title={`${vezes} ocorrências consecutivas`}>
                      ×{vezes}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--sga-fg-3-texto)' }}>
                  {i.conexao_nome ?? i.origem}
                  {i.codigo ? ` · ${i.codigo}` : ''}
                </div>
              </div>
              <span className="mono" style={{ fontSize: 11.5, color: 'var(--sga-fg-3-texto)' }}>
                {hhmm(i.created_at)}
              </span>
            </div>
          ))}
        </Card>
      </div>
    </>
  );
}

function iconeContainer(servico: string) {
  const mapa: Record<string, string> = {
    openwa: 'mdi:whatsapp',
    evolution: 'mdi:api',
    telegram: 'mdi:telegram',
    cloud: 'mdi:check-decagram',
    form: 'mdi:text-box-search-outline',
    gateway: 'mdi:server-network',
  };
  return mapa[servico] ?? 'mdi:server-network';
}

function corDoNivel(nivel: Parameters<typeof kindDoNivel>[0]) {
  const kind = kindDoNivel(nivel);
  return kind === 'err'
    ? 'var(--sga-error)'
    : kind === 'warn'
      ? 'var(--sga-warning)'
      : 'var(--sga-tag-text)';
}
